import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import NotAvailable from '../Components/NotAvailable';
import { listClients, useNonMmlAdminAccess } from '../utils/oidcClients';
import {
    MAX_LIMIT,
    PARTNER_STATUSES,
    fullName,
    getPartnerInquiry,
    idOf,
    isPartnerInquiry,
    isPlainEmail,
    listAdminUsers,
    organizationOf,
    updatePartnerInquiry,
} from '../utils/partnerInquiries';

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const userLabel = (user) => (user.name ? `${user.name} (${user.email})` : user.email);

// admin#181 — one partner inquiry: the message (read-only), its status, notes
// and owner (editable), and the partner clients registered for it.
function PartnerInquiriesView() {
    const { id } = useParams();
    const allowed = useNonMmlAdminAccess();
    const [inquiry, setInquiry] = useState(null);
    const [inquiryError, setInquiryError] = useState('');
    const [clients, setClients] = useState([]);
    const [clientsError, setClientsError] = useState('');
    const [admins, setAdmins] = useState([]);
    const [adminsError, setAdminsError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [status, setStatus] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [assignedTo, setAssignedTo] = useState('');

    const resetForm = useCallback((contact) => {
        setInquiry(contact);
        setStatus(contact.status || 'pending');
        setAdminNotes(contact.adminNotes || '');
        setAssignedTo(idOf(contact.assignedTo));
    }, []);

    // Loaded independently: a failed clients or admins read mustn't hide the
    // inquiry, or be shown as "no linked clients".
    useEffect(() => {
        if (!allowed) return undefined;
        let active = true;
        setLoading(true);
        setInquiry(null);
        setInquiryError('');
        setClients([]);
        setClientsError('');
        setAdminsError('');
        (async () => {
            const [inquiryResult, clientsResult, adminsResult] = await Promise.allSettled([
                getPartnerInquiry(id),
                listClients({ partnerContactId: id, limit: MAX_LIMIT }),
                listAdminUsers(),
            ]);
            if (!active) return;
            if (inquiryResult.status !== 'fulfilled') {
                setInquiryError(inquiryResult.reason?.message || 'Failed to load inquiry.');
            } else if (!isPartnerInquiry(inquiryResult.value)) {
                // GET /shopping/contact/:id returns any contact type.
                setInquiryError('This contact isn\'t a partner inquiry.');
            } else {
                resetForm(inquiryResult.value);
            }
            if (clientsResult.status === 'fulfilled') {
                // An API without api#766 ignores partnerContactId and returns
                // every client; show only the ones actually linked here.
                setClients((clientsResult.value?.clients || []).filter((c) => idOf(c.partnerContactId) === id));
            } else {
                setClientsError(clientsResult.reason?.message || 'Failed to load linked clients.');
            }
            if (adminsResult.status === 'fulfilled') {
                setAdmins(adminsResult.value);
            } else {
                setAdminsError(adminsResult.reason?.message || 'Failed to load admins.');
            }
            setLoading(false);
        })();
        return () => { active = false; };
    }, [allowed, id, resetForm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        const changes = {};
        if (status !== inquiry.status) changes.status = status;
        if (adminNotes !== (inquiry.adminNotes || '')) changes.adminNotes = adminNotes;
        if (assignedTo && assignedTo !== idOf(inquiry.assignedTo)) changes.assignedTo = assignedTo;
        if (Object.keys(changes).length === 0) {
            toast.info('Nothing changed.');
            return;
        }
        setSaving(true);
        try {
            const updated = await updatePartnerInquiry(id, changes);
            resetForm(updated);
            toast.success('Inquiry updated.');
        } catch (err) {
            toast.error(err.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    if (allowed === false) return <NotAvailable />;

    // The current owner may not be in the admin list (a superadmin, or a
    // since-deactivated admin); keep them selectable so the form shows them.
    const currentOwner = inquiry?.assignedTo && typeof inquiry.assignedTo === 'object' ? inquiry.assignedTo : null;
    const ownerOptions = currentOwner && !admins.some((a) => a._id === currentOwner._id)
        ? [currentOwner, ...admins]
        : admins;

    const rows = inquiry ? [
        ['Name', fullName(inquiry) || '—'],
        ['Organization', organizationOf(inquiry) || '—'],
        ['Email', isPlainEmail(inquiry.email)
            ? <a key="email" href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
            : inquiry.email || '—'],
        ['Phone', inquiry.phone || '—'],
        ['Subject', inquiry.subject || '—'],
        ['Submitted', formatDateTime(inquiry.createdAt)],
        ['Last updated', formatDateTime(inquiry.updatedAt)],
    ] : [];

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">
                                {(inquiry && (organizationOf(inquiry) || fullName(inquiry))) || 'Partner Inquiry'}
                            </h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            <Link to="/partnerInquiries" className="btn btn-secondary">Back</Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {loading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : inquiryError ? (
                        <div className="alert alert-danger txt_lft" role="alert">
                            Couldn&apos;t load this inquiry: {inquiryError}{' '}
                            <Link to="/partnerInquiries">Back to Partner Inquiries</Link>
                        </div>
                    ) : (
                        <>
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <tbody>
                                                {rows.map(([label, value]) => (
                                                    <tr key={label}>
                                                        <td style={{ width: '200px' }}><span className="fwt_600">{label}</span></td>
                                                        <td>{value}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td><span className="fwt_600">Message</span></td>
                                                    <td data-testid="inquiry-message" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {inquiry.message}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-body txt_lft">
                                    <h5 className="mb-3">Follow-up</h5>
                                    <form onSubmit={handleSubmit} noValidate>
                                        <div className="form-group">
                                            <label htmlFor="inquiry-status" className="fwt_600">Status</label>
                                            <select
                                                id="inquiry-status"
                                                className="form-control"
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                            >
                                                {PARTNER_STATUSES.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="inquiry-owner" className="fwt_600">Owner</label>
                                            <select
                                                id="inquiry-owner"
                                                className="form-control"
                                                value={assignedTo}
                                                onChange={(e) => setAssignedTo(e.target.value)}
                                            >
                                                {/* The API ignores an empty assignedTo, so an owner can be
                                                    changed but not removed; offer "Unassigned" only until one is set. */}
                                                {!idOf(inquiry.assignedTo) && <option value="">Unassigned</option>}
                                                {ownerOptions.map((user) => (
                                                    <option key={user._id} value={user._id}>{userLabel(user)}</option>
                                                ))}
                                            </select>
                                            {adminsError && (
                                                <small className="form-text text-danger">
                                                    Couldn&apos;t load the admin list: {adminsError}
                                                </small>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="inquiry-notes" className="fwt_600">Admin notes</label>
                                            <textarea
                                                id="inquiry-notes"
                                                className="form-control"
                                                rows={4}
                                                value={adminNotes}
                                                onChange={(e) => setAdminNotes(e.target.value)}
                                            />
                                            <small className="form-text text-muted">
                                                Internal only. The partner is not emailed when this screen changes.
                                            </small>
                                        </div>

                                        <button type="submit" className="btn btn-blue" disabled={saving}>
                                            Save changes
                                        </button>
                                    </form>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-body txt_lft">
                                    <h5 className="mb-3">Linked clients</h5>
                                    {clientsError ? (
                                        <div className="alert alert-warning" role="alert">
                                            Couldn&apos;t load the linked clients: {clientsError}
                                        </div>
                                    ) : clients.length === 0 ? (
                                        <p className="text-muted mb-0">
                                            No partner clients are linked to this inquiry. Pick it as the
                                            partner inquiry when registering or editing a client.
                                        </p>
                                    ) : (
                                        <ul className="mb-0" data-testid="linked-clients">
                                            {clients.map((client) => (
                                                <li key={client.clientId}>
                                                    <Link to={`/oidcClientsView/${encodeURIComponent(client.clientId)}`}>
                                                        {client.name}
                                                    </Link>{' '}
                                                    <code>{client.clientId}</code>{' '}
                                                    <span className="text-muted">({client.status})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default PartnerInquiriesView;
